import type { AppAbility } from './permissions'
import { labels, type Resource } from './resource'

/**
 * Guides (คู่มือ): how the work is done in the apps, written once beside the resources and read by staff (the "?" on a
 * page, the guides page) and by an AI assistant over MCP, so people and the assistant learn the same rules
 * (docs/guides.md). The text is Thai markdown. The apps' own words are named by tokens instead of typed, so a renamed
 * field is renamed in every guide and a removed one fails the test (packages/i18n/src/guides.test.ts):
 *
 *   {field:startDate}         a field of the guide's resource     {field:hr.employee.managerId}    another's
 *   {value:status.pending}    one of a field's options            {value:hr.leave.status.pending}
 *   {resource:hr.leaveType}   a resource's name
 *   {t:doc.actions.submit}    any shared or module text (a button, a status): `{t:hr:leave.actions.approve}`
 *   {guide:hr.application}    another guide, by its title: a link in the apps, its key for an assistant
 *
 * Only text every app and the API load: the shared namespace and the modules' (not an app's own, like Thananan OS's).
 *
 * A guide is of one of three kinds (`guideKind`): a page's (its key a resource's: how that page is used), a topic (how
 * something works over several pages) or a task (what to do when something happens, from start to end and by whom,
 * each step naming the guide that tells how, so the how is written once).
 */
export interface GuideSection {
  title: string
  /** Only for those who may do this (`<resource>:<action>`, in any scope); none: everyone who reads the guide */
  can?: string
  body: string
}

export type GuideKind = 'page' | 'topic' | 'task'

export interface Guide {
  /** A resource's key, the guide of its pages; or `<app>.<topic>` for work over several */
  key: string
  /** A topic's or a task's title; a resource's guide is titled with its name */
  title?: string
  /** `task`: what to do when something happens, step by step; none: a page's guide, or a topic */
  kind?: 'task'
  /** What the work is for, in a sentence or two: shown first, and what an assistant reads to choose a guide */
  summary: string
  /** Other resources whose pages show it too */
  pages?: readonly string[]
  sections: readonly GuideSection[]
}

export interface GuideToken {
  /** As written: `{field:startDate}` */
  raw: string
  kind: string
  name: string
}

export interface GuideContext {
  /** The guide's own resource, whose fields the short names are */
  resource?: Resource
  /** The registry's lookup (`findResource` in `@tnn/domain`) */
  find: (key: string) => Resource | undefined
  /** Every guide, that `{guide:…}` names (`guides` in `@tnn/domain/guides`) */
  guides?: readonly Guide[]
}

export const guideKind = (guide: Guide, find: GuideContext['find']): GuideKind => guide.kind ?? (find(guide.key) ? 'page' : 'topic')

/** A guide's title in the reader's words: a topic's or a task's own, else its resource's name */
export function guideTitle(guide: Guide, { find, t }: Pick<GuideContext, 'find'> & { t: (key: string) => string }): string {
  const resource = find(guide.key)
  return guide.title ?? (resource ? t(labels.title(resource)) : guide.key)
}

/** The guides shown with a resource's pages: its own first, then the topics and tasks that name it */
export const guidesOn = (guides: readonly Guide[], key: string): Guide[] => [
  ...guides.filter((g) => g.key === key),
  ...guides.filter((g) => g.key !== key && g.pages?.includes(key)),
]

const tokenPattern = /\{([a-z]+):([^{}\s]+)\}/g

export function guideTokens(text: string): GuideToken[] {
  return [...text.matchAll(tokenPattern)].map(([raw, kind = '', name = '']) => ({ raw, kind, name }))
}

/** `<app>.<resource>.<rest…>` or just `<rest…>` (the guide's own resource), `rest` being `count` parts */
function within(name: string, count: number, ctx: GuideContext) {
  const parts = name.split('.')
  if (parts.length === count) return { resource: ctx.resource, rest: parts }
  if (parts.length === count + 2) return { resource: ctx.find(parts.slice(0, 2).join('.')), rest: parts.slice(2) }
  return { resource: undefined, rest: parts }
}

/** The i18n key a token stands for, the guide `{guide:…}` names, or what is wrong with it */
export function guideTokenKey({ kind, name }: GuideToken, ctx: GuideContext): { key: string } | { guide: Guide } | { problem: string } {
  switch (kind) {
    case 't':
      return { key: name }
    case 'guide': {
      const guide = ctx.guides?.find((g) => g.key === name)
      return guide ? { guide } : { problem: `no guide "${name}"` }
    }
    case 'resource': {
      const resource = ctx.find(name)
      return resource ? { key: labels.title(resource) } : { problem: `no resource "${name}"` }
    }
    case 'field': {
      const { resource, rest } = within(name, 1, ctx)
      const field = resource?.field(rest[0] ?? '')
      return field ? { key: field.label } : { problem: `no field "${name}"` }
    }
    case 'value': {
      const { resource, rest } = within(name, 2, ctx)
      const [field = '', value = ''] = rest
      if (!resource?.field(field)?.options?.includes(value)) return { problem: `no option "${name}"` }
      return { key: labels.option(resource, field, value) }
    }
    default:
      return { problem: `unknown token "{${kind}:…}"` }
  }
}

/**
 * Text with each token in the reader's words (`t` in their language); a token naming nothing stays as written. A guide
 * named is its title, or what `link` makes of it (Markdown's link in the apps)
 */
export function renderGuideText(
  text: string,
  ctx: GuideContext & { t: (key: string) => string; link?: (guide: Guide, title: string) => string },
): string {
  return text.replace(tokenPattern, (raw, kind: string, name: string) => {
    const found = guideTokenKey({ raw, kind, name }, ctx)
    if ('problem' in found) return raw
    if ('key' in found) return ctx.t(found.key)
    const title = guideTitle(found.guide, ctx)
    return ctx.link ? ctx.link(found.guide, title) : title
  })
}

/** A section's `can` as CASL reads it: `hr.leave:recordFor` → ['recordFor', 'hr.leave'] */
export function sectionPermission(can: string): [action: string, subject: string] {
  const at = can.lastIndexOf(':')
  return [can.slice(at + 1), can.slice(0, at)]
}

/** The sections someone reads: those for everyone, and those whose `can` their permissions give (in any scope) */
export function sectionsFor(guide: Guide, ability: AppAbility): GuideSection[] {
  return guide.sections.filter((section) => !section.can || ability.can(...sectionPermission(section.can)))
}
